import {ClassifiedError, httpErrorKind, isAbortError} from "./errorKind";

// In development, use relative URLs so requests go through the webpack dev server
// proxy (avoids cross-origin issues in the Office.js WebView).
// In production, use the full LIBRECHAT_API_URL.
const BASE_URL =
  process.env.NODE_ENV === "production"
    ? (process.env.LIBRECHAT_API_URL || "").replace(/\/+$/, "")
    : "";

export interface Agent {
  id: string;
  name: string;
}

const DEFAULT_REQUEST_TIMEOUT_SECONDS = 5 * 60;

function parseTimeoutSeconds(): number {
  const raw = process.env.REQUEST_TIMEOUT_SECONDS;
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) && parsed > 0
    ? parsed
    : DEFAULT_REQUEST_TIMEOUT_SECONDS;
}

/**
 * How long a single request may take before it is aborted. Exported so the UI
 * can warn as the ceiling approaches instead of just failing at it.
 * Configurable via the REQUEST_TIMEOUT_SECONDS env var (default 300s).
 */
export const REQUEST_TIMEOUT_MS = parseTimeoutSeconds() * 1000;

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

const TIMEOUT_MESSAGE =
  "Request timed out — the server took too long to respond. Try a simpler request or check the LibreChat instance.";

function logApiDiagnostic(
  event: string,
  details: Record<string, unknown>,
): void {
  console.error(`[librechat-api] ${event}`, details);
}

function responseContentType(response: Response): string {
  return response.headers?.get?.("content-type") || "unknown";
}

export interface SendMessageOptions {
  agentId: string;
  messages: ChatMessage[];
  apiKey?: string;
  accessToken?: string;
  stream?: boolean;
  onChunk?: (text: string) => void;
  /** Aborts the request; whatever streamed in before the abort is returned. */
  signal?: AbortSignal;
}

function authHeaders(
  token: string,
  includeContentType = true,
): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
}

export async function listAgents(apiKey: string): Promise<Agent[]> {
  let res: Response;
  try {
    res = await fetch(`${BASE_URL}/api/agents/v1/models`, {
      method: "GET",
      headers: authHeaders(apiKey, false),
    });
  } catch (error) {
    logApiDiagnostic("list-agents network error", {
      method: "GET",
      path: "/api/agents/v1/models",
      error: errorNameOf(error),
    });
    throw new ClassifiedError(
      "network",
      `Network error — check that your LibreChat instance is reachable at ${BASE_URL}`,
    );
  }

  if (!res.ok) {
    logApiDiagnostic("list-agents HTTP error", {
      method: "GET",
      path: "/api/agents/v1/models",
      status: res.status,
      statusText: res.statusText,
      responseContentType: responseContentType(res),
    });
    throw new ClassifiedError(
      httpErrorKind(res.status),
      `Failed to list agents: ${res.status} ${res.statusText}`,
    );
  }

  const data = await res.json();
  // The /models endpoint returns an OpenAI-compatible model list
  const models: Array<{id: string; name?: string}> = data.data ?? [];
  return models.map((m) => ({id: m.id, name: m.name ?? m.id}));
}

export async function sendMessage(
  options: SendMessageOptions,
): Promise<string> {
  const {
    agentId,
    messages,
    apiKey,
    accessToken,
    stream = false,
    onChunk,
    signal,
  } = options;

  if (signal?.aborted) return "";

  const token = accessToken ?? apiKey ?? "";
  const url = `${BASE_URL}/api/agents/v1/chat/completions`;
  const headers = authHeaders(token);
  const body = JSON.stringify({
    model: agentId,
    messages,
    stream,
  });

  // Allow up to 5 minutes for the LLM to respond (large context + multi-action).
  // The timer is cleared in the outer `finally`, not once the headers land: a
  // streamed reply spends nearly all of its time arriving, so a ceiling that
  // stopped at the response headers left a stalled stream hanging forever.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const abortFromCaller = () => controller.abort();
  signal?.addEventListener("abort", abortFromCaller);

  try {
    let res: Response;
    try {
      res = await fetch(url, {
        method: "POST",
        headers,
        body,
        signal: controller.signal,
      });
    } catch (err: unknown) {
      if (signal?.aborted) return "";
      if (isAbortError(err)) {
        logApiDiagnostic("request timeout", {
          method: "POST",
          path: "/api/agents/v1/chat/completions",
          timeoutMs: REQUEST_TIMEOUT_MS,
        });
        throw new ClassifiedError("timeout", TIMEOUT_MESSAGE);
      }
      logApiDiagnostic("request network error", {
        method: "POST",
        path: "/api/agents/v1/chat/completions",
        error: errorNameOf(err),
      });
      throw new ClassifiedError(
        "network",
        `Network error — check that your LibreChat instance is reachable at ${BASE_URL}`,
      );
    }

    if (!res.ok) {
      logApiDiagnostic("request HTTP error", {
        method: "POST",
        path: "/api/agents/v1/chat/completions",
        status: res.status,
        statusText: res.statusText,
        responseContentType: responseContentType(res),
      });
      throw new ClassifiedError(
        httpErrorKind(res.status),
        `API error: ${res.status} ${res.statusText}`,
      );
    }

    // Reading the body is a second place the request can die: the connection
    // drops mid-stream, or the 5-minute ceiling lands after the headers have
    // already arrived. Both used to escape unclassified.
    try {
      if (stream && onChunk && res.body) {
        return await readStream(res.body, onChunk, signal);
      }

      const data = await res.json();
      return extractResponseText(data);
    } catch (err: unknown) {
      if (signal?.aborted) return "";
      // The only abort left is our own timeout — the caller's was handled
      // above — so this is the ceiling, not a broken stream.
      if (isAbortError(err)) {
        logApiDiagnostic("response body timeout", {
          method: "POST",
          path: "/api/agents/v1/chat/completions",
          timeoutMs: REQUEST_TIMEOUT_MS,
        });
        throw new ClassifiedError("timeout", TIMEOUT_MESSAGE);
      }
      logApiDiagnostic("response body error", {
        method: "POST",
        path: "/api/agents/v1/chat/completions",
        error: errorNameOf(err),
      });
      // Named, not quoted: a malformed body makes `res.json()` throw a
      // SyntaxError carrying a snippet of that body — which is the model's
      // reply, derived from the workbook. This message reaches a Tempo span,
      // and ADR-0002 keeps assistant text off spans entirely.
      throw new ClassifiedError(
        "stream",
        `Response stream failed (${errorNameOf(err)})`,
      );
    }
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abortFromCaller);
  }
}

/** What kind of thing went wrong, with none of the detail it came with. */
function errorNameOf(err: unknown): string {
  return err instanceof Error ? err.name : typeof err;
}

async function readStream(
  body: ReadableStream<Uint8Array>,
  onChunk: (text: string) => void,
  signal?: AbortSignal,
): Promise<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let fullText = "";
  let buffer = "";

  while (true) {
    let chunk: ReadableStreamReadResult<Uint8Array>;
    try {
      chunk = await reader.read();
    } catch (err) {
      // An abort tears the stream down mid-read; hand back what arrived.
      if (signal?.aborted) break;
      throw err;
    }
    if (chunk.done) break;

    buffer += decoder.decode(chunk.value, {stream: true});
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed === "data: [DONE]") continue;

      if (trimmed.startsWith("data: ")) {
        try {
          const json = JSON.parse(trimmed.slice(6));
          const text = extractStreamChunkText(json);
          if (text) {
            fullText += text;
            onChunk(text);
          }
        } catch {
          // Skip malformed JSON chunks
        }
      }
    }
  }

  return fullText;
}

function extractResponseText(data: Record<string, unknown>): string {
  // Chat Completions format: choices[0].message.content
  const choices = data.choices as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(choices) && choices.length > 0) {
    const message = choices[0].message as Record<string, unknown> | undefined;
    if (message && typeof message.content === "string") {
      return message.content;
    }
  }

  // Fallback
  if (typeof data.text === "string") return data.text;
  if (typeof data.content === "string") return data.content;

  return JSON.stringify(data);
}

function extractStreamChunkText(json: Record<string, unknown>): string {
  // Chat Completions streaming: choices[0].delta.content
  const choices = json.choices as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(choices) && choices.length > 0) {
    const delta = choices[0].delta as Record<string, unknown> | undefined;
    if (delta && typeof delta.content === "string") {
      return delta.content;
    }
  }

  return "";
}
